import { createAsyncThunk } from '@reduxjs/toolkit';
import { coursesApi } from '../../api/coursesApi/coursesApi';
import { lessonsApi } from '../../api/lessonsApi/lessonsApi';

// Get all courses with filters and pagination
export const getCoursesAsyncThunk = createAsyncThunk(
  'course/getCourses',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await coursesApi.getCourses(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch courses'
      );
    }
  }
);

// Get courses with filters (for search functionality)
export const fetchCoursesWithFiltersAsyncThunk = createAsyncThunk(
  'course/fetchCoursesWithFilters',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { filters, pagination } = state.course;
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        category: filters.category,
        level: filters.level,
        format: filters.format,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      if (filters.isActive !== undefined) {
        params.isActive = filters.isActive;
      }
      
      const response = await coursesApi.getCourses(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch courses'
      );
    }
  }
);

// Get course by ID
export const getCourseByIdAsyncThunk = createAsyncThunk(
  'course/getCourseById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await coursesApi.getCourseById(id);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch course'
      );
    }
  }
);

// Create course
export const createCourseAsyncThunk = createAsyncThunk(
  'course/createCourse',
  async (courseData, { rejectWithValue }) => {
    try {
      const response = await coursesApi.createCourse(courseData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create course'
      );
    }
  }
);

// Update course
export const updateCourseAsyncThunk = createAsyncThunk(
  'course/updateCourse',
  async ({ id, courseData }, { rejectWithValue }) => {
    try {
      const response = await coursesApi.updateCourse(id, courseData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update course'
      );
    }
  }
);

// Delete course
export const deleteCourseAsyncThunk = createAsyncThunk(
  'course/deleteCourse',
  async (id, { rejectWithValue }) => {
    try {
      const response = await coursesApi.deleteCourse(id);
      return id; // Return the ID of the deleted course
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete course'
      );
    }
  }
);

// Get all lessons in a course
export const getAllLessonsInCourseAsyncThunk = createAsyncThunk(
  'course/getAllLessonsInCourse',
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await coursesApi.getAllLessonsInCourse(courseId);
      return { courseId, lessons: response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load course lessons'
      );
    }
  }
);

// Add existing lesson to course
export const addExistingLessonToCourseAsyncThunk = createAsyncThunk(
  'course/addExistingLessonToCourse',
  async ({ courseId, lessonId }, { rejectWithValue }) => {
    try {
      const response = await coursesApi.addExistingLessonToCourse(courseId, lessonId);
      return { courseId, lesson: response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to add lesson to course'
      );
    }
  }
);

// Create new lesson and add to course
export const createNewLessonAndAddToCourseAsyncThunk = createAsyncThunk(
  'course/createNewLessonAndAddToCourse',
  async ({ courseId, lessonData }, { rejectWithValue }) => {
    try {
      const response = await coursesApi.createNewLessonAndAddToCourse(courseId, lessonData);
      return { courseId, lesson: response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create and add lesson'
      );
    }
  }
);

// Update lesson in course
export const updateLessonInCourseAsyncThunk = createAsyncThunk(
  'course/updateLessonInCourse',
  async ({ courseId, lessonId, lessonData }, { rejectWithValue }) => {
    try {
      const response = await coursesApi.updateLessonInCourse(courseId, lessonId, lessonData);
      return { courseId, lesson: response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update lesson in course'
      );
    }
  }
);

// Remove lesson from course
export const removeLessonFromCourseAsyncThunk = createAsyncThunk(
  'course/removeLessonFromCourse',
  async ({ courseId, lessonId }, { rejectWithValue }) => {
    try {
      await coursesApi.removeLessonFromCourse(courseId, lessonId);
      return { courseId, lessonId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to remove lesson from course'
      );
    }
  }
);

// Get course with lessons and HLS video links
export const getCourseWithLessonsAndHSLVideoLinksAsyncThunk = createAsyncThunk(
  'course/getCourseWithLessonsAndHSLVideoLinks',
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await coursesApi.getCourseWithLessonsAndHSLVideoLinks(courseId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load course videos'
      );
    }
  }
);
